/**
 * 결제 수단·청구 일정 — 서버가 주는 값의 모양
 * ---------------------------------------------------------------------------
 * 날짜 숫자를 화면에 박지 않기 위한 통로다. 값은 전부
 * `GET /billing/payment-schedule` 이 준다(공통코드 BILLING_POLICY +
 * 기업별 납기 billing_preference.due_days).
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient, type NormalizedError } from '../../lib/api-client';

export interface PaymentSchedule {
  /** 기업이 고른 결제 수단 */
  payMethod: 'TRANSFER' | 'CARD';
  /** 카드를 고를 수 있는가. PG 계약 전에는 false 라 화면에서 잠긴다 */
  cardEnabled: boolean;
  /** 청구서가 나가는 날 — 매월 N일 */
  invoiceIssueDay: number;
  /** 납기 — 발행일로부터 N일 */
  dueDays: number;
  /** 세금계산서 발행 기한 — 다음 달 N일 (법정 기한) */
  taxInvoiceByDay: number;
  /** 납기를 N일 넘기면 사용이 정지된다 */
  suspendAfterDays: number;
  /** 등록된 카드(빌링키)가 있는가 */
  hasActiveCard: boolean;
  /**
   * 결제 수단을 **실제로 등록했는가.**
   *
   * `payMethod` 만 보고 판단하면 안 된다 — 서버의 기본값이 TRANSFER 라,
   * 아직 아무것도 하지 않은 기업도 「계좌이체」로 보인다.
   */
  payMethodRegistered: boolean;
  /**
   * 계좌이체로 보낼 때 통장에 찍히는 이름. 적지 않았으면 null.
   *
   * 상호와 다른 일이 흔하다(대표자 개인 이름·경리 담당자 이름). 이 값이
   * 없으면 운영자가 입금 내역을 보고도 어느 기업이 낸 돈인지 모른다.
   */
  depositorName: string | null;
  /** 결제 수단을 고른 시각. 아직 고르지 않았으면 null */
  payMethodConfirmedAt: string | null;
}

export function usePaymentSchedule() {
  return useQuery<PaymentSchedule, NormalizedError>({
    queryKey: ['billing', 'payment-schedule'],
    queryFn: async () =>
      (await apiClient.get<PaymentSchedule>('/billing/payment-schedule')).data,
  });
}
